import { useState } from 'react'
import { type Control, Controller } from 'react-hook-form'
import { Pressable, StyleSheet, View } from 'react-native'
import {
    Dialog,
    HelperText,
    TextInput as PaperTextInput,
    Switch,
    Text,
    useTheme,
} from 'react-native-paper'
import ColorPicker, { type ColorFormatsObject, HueCircular, Panel1 } from 'reanimated-color-picker'
import type { TagFormData } from '@/types/tags'
import { Button } from '@/ui/Button'
import { TextInput as FormTextInput, TextAreaInput } from '@/ui/form'
import { PickerDialog } from '@/ui/PickerDialog'

const DEFAULT_TAG_COLOR = '#3B82F6'

const PRESET_SWATCHES = [
    '#EF4444',
    '#F97316',
    '#F59E0B',
    '#EAB308',
    '#84CC16',
    '#22C55E',
    '#10B981',
    '#14B8A6',
    '#06B6D4',
    '#3B82F6',
    '#6366F1',
    '#8B5CF6',
    '#A855F7',
    '#EC4899',
    '#F43F5E',
    '#64748B',
]

function sanitizeHexValue(raw: string): string {
    const trimmed = raw.trim()
    if (trimmed.length === 0) return ''
    const prefixed = trimmed.startsWith('#') ? trimmed : `#${trimmed}`
    return prefixed.toUpperCase()
}

function displayHexColor(raw?: string | null): string {
    if (!raw || raw.trim().length === 0) return DEFAULT_TAG_COLOR
    return sanitizeHexValue(raw)
}

interface ColorPickerPopoverProps {
    visible: boolean
    onDismiss: () => void
    color: string
    onChange: (hex: string) => void
}

function ColorPickerPopover({ visible, onDismiss, color, onChange }: ColorPickerPopoverProps) {
    const theme = useTheme()

    const handleLiveChange = (colors: ColorFormatsObject) => {
        if (colors?.hex) onChange(sanitizeHexValue(colors.hex))
    }

    return (
        <PickerDialog visible={visible} onDismiss={onDismiss}>
            <Dialog.Title style={{ color: theme.colors.onSurface }}>Pick a color</Dialog.Title>
            <Dialog.Content>
                <View style={styles.popoverBody}>
                    <Text variant="labelMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                        Presets
                    </Text>
                    <View style={styles.presetGrid}>
                        {PRESET_SWATCHES.map(swatch => {
                            const isSelected = swatch.toUpperCase() === color.toUpperCase()
                            return (
                                <Pressable
                                    key={swatch}
                                    onPress={() => onChange(swatch)}
                                    accessibilityLabel={`Use color ${swatch}`}
                                    accessibilityRole="button"
                                    style={[
                                        styles.presetSwatch,
                                        { backgroundColor: swatch },
                                        isSelected && {
                                            borderColor: theme.colors.onSurface,
                                            borderWidth: 2,
                                        },
                                    ]}
                                />
                            )
                        })}
                    </View>

                    <Text
                        variant="labelMedium"
                        style={[styles.customLabel, { color: theme.colors.onSurfaceVariant }]}
                    >
                        Custom
                    </Text>
                    <ColorPicker
                        value={color}
                        sliderThickness={16}
                        thumbSize={20}
                        onChange={handleLiveChange}
                        style={styles.colorPickerBlock}
                        boundedThumb
                    >
                        <HueCircular
                            containerStyle={{ justifyContent: 'center' }}
                            thumbShape="pill"
                        >
                            <Panel1 style={styles.colorPanel} />
                        </HueCircular>
                    </ColorPicker>
                </View>
            </Dialog.Content>
            <Dialog.Actions style={styles.popoverActions}>
                <Button title="Done" mode="contained" onPress={onDismiss} />
            </Dialog.Actions>
        </PickerDialog>
    )
}

interface TagFormFieldsProps {
    control: Control<TagFormData>
    isTwoColumn?: boolean
}

export function TagFormFields({ control, isTwoColumn = false }: TagFormFieldsProps) {
    const theme = useTheme()
    const [pickerVisible, setPickerVisible] = useState(false)

    const nameAndStatusField = (
        <View style={styles.nameRow}>
            <FormTextInput
                name="name"
                control={control}
                label="Tag Name"
                hint="Lowercase letters, numbers, and hyphens only"
                autoCapitalize="none"
                autoCorrect={false}
                wrapperProps={{ style: styles.nameInput }}
            />
            <View style={styles.inactiveToggle}>
                <Text variant="labelLarge" style={styles.inactiveLabel}>
                    Inactive
                </Text>
                <Controller
                    control={control}
                    name="inactive"
                    render={({ field: { value, onChange } }) => (
                        <Switch
                            value={Boolean(value)}
                            onValueChange={onChange}
                            accessibilityLabel="Mark tag inactive"
                        />
                    )}
                />
            </View>
        </View>
    )

    const descriptionField = (
        <TextAreaInput
            name="description"
            control={control}
            label="Description (optional)"
            hint="Brief description of what this tag represents"
            multiline
            numberOfLines={3}
        />
    )

    const colorField = (
        <Controller
            control={control}
            name="color"
            render={({
                field: { value = DEFAULT_TAG_COLOR, onChange, onBlur },
                fieldState: { error },
            }) => {
                const handleManualChange = (text: string) => {
                    if (text.trim().length === 0) {
                        onChange('')
                        return
                    }
                    onChange(sanitizeHexValue(text))
                }

                const displayColor = displayHexColor(value)

                const closePicker = () => {
                    setPickerVisible(false)
                    onBlur()
                }

                return (
                    <View style={styles.colorField}>
                        <View style={styles.colorInputRow}>
                            <Pressable
                                onPress={() => setPickerVisible(true)}
                                accessibilityRole="button"
                                accessibilityLabel="Choose color"
                                style={({ pressed }) => [
                                    styles.colorChip,
                                    {
                                        backgroundColor: displayColor,
                                        borderColor: theme.colors.outline,
                                    },
                                    pressed && {
                                        borderColor: theme.colors.primary,
                                        borderWidth: 2,
                                    },
                                ]}
                            />
                            <PaperTextInput
                                label="Color"
                                mode="outlined"
                                value={value}
                                onChangeText={handleManualChange}
                                onBlur={onBlur}
                                autoCapitalize="characters"
                                autoCorrect={false}
                                maxLength={7}
                                style={styles.colorHexInput}
                                accessibilityLabel="Color"
                                testID="tag-color-input"
                            />
                        </View>
                        <Text
                            variant="bodySmall"
                            style={[styles.colorHint, { color: theme.colors.onSurfaceVariant }]}
                        >
                            Tap the swatch to pick a color, or enter a hex value (e.g. #FF5733)
                        </Text>

                        {error ? <HelperText type="error">{error.message}</HelperText> : null}

                        <ColorPickerPopover
                            visible={pickerVisible}
                            onDismiss={closePicker}
                            color={displayColor}
                            onChange={hex => onChange(sanitizeHexValue(hex))}
                        />
                    </View>
                )
            }}
        />
    )

    if (isTwoColumn) {
        return (
            <View style={[styles.container, styles.twoColumnContainer]}>
                <View style={[styles.column, styles.primaryColumn]}>
                    {nameAndStatusField}
                    {descriptionField}
                </View>
                <View style={[styles.column, styles.secondaryColumn]}>{colorField}</View>
            </View>
        )
    }

    return (
        <View style={styles.container}>
            {nameAndStatusField}
            {colorField}
            {descriptionField}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        gap: 16,
    },
    twoColumnContainer: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 24,
    },
    column: {
        flex: 1,
        gap: 16,
        minWidth: 0,
    },
    primaryColumn: {
        flex: 1,
    },
    secondaryColumn: {
        maxWidth: 320,
    },
    nameRow: {
        flexDirection: 'row',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    nameInput: {
        flex: 1,
    },
    inactiveToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    inactiveLabel: {
        fontWeight: '500',
    },
    colorField: {
        gap: 8,
    },
    colorInputRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    colorChip: {
        width: 44,
        height: 44,
        borderRadius: 10,
        borderWidth: 1,
    },
    colorHexInput: {
        flex: 1,
    },
    colorHint: {
        marginTop: 2,
    },
    popoverBody: {
        gap: 12,
    },
    presetGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    presetSwatch: {
        width: 36,
        height: 36,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(0,0,0,0.08)',
    },
    customLabel: {
        marginTop: 8,
    },
    colorPickerBlock: {
        gap: 12,
    },
    colorPanel: {
        borderRadius: 16,
        width: '70%',
        height: '70%',
        alignSelf: 'center',
    },
    popoverActions: {
        justifyContent: 'flex-end',
        paddingHorizontal: 16,
    },
})
